import LivechatService from '@/services/livechat';

const state = {
    organization: null,
    isLoading: false,
}

const getters = {
    organization: state => state.organization,
    agents: state => state.organization.agents,
    groups: state => state.organization?.groups,
    isLoading: state => state.isLoading,
}

const actions = {
    async init({ dispatch, state }, uniqueBotId) {
        state.isLoading = true;
        await dispatch('fetchOrganization', uniqueBotId);
        state.isLoading = false;
    },
    async fetchOrganization({ commit }, uniqueBotId) {
        const organization = await LivechatService.getOrganizatioByBotId(uniqueBotId);
        commit('setOrganization', organization);
    },
    async removeAgentGroup({ commit, state }, { agentUuid, groupUuid} ) {
        try {
            const agent = await LivechatService.removeAgentGroup(agentUuid, groupUuid);
            const agents = state.organization.agents;
            agents.find(a => a.uuid === agentUuid).groups = [...agent.groups]
            commit('setOrganization', {
                ...state.organization,
                agents: [...agents]
            });
        } catch (e) {
            console.error(e);
        }
    },

    async addAgentGroups({ commit, state }, { agentUuid, groups} ) {
        try {
            const agent = await LivechatService.addAgentGroups(agentUuid, groups);
            const agents = state.organization.agents;
            agents.find(a => a.uuid === agentUuid).groups = [...agent.groups]
            commit('setOrganization', {
                ...state.organization,
                agents: [...agents]
            });
        } catch (e) {
            console.error(e);
        }
    },
    /**
     * 
     * @param {object} context 
     * @param {object} payload - The payload object.
     * @param {string} payload.username - email of the agent.
     * @param {string[]} payload.groups - The UUIDs of the groups.
     */
    async createAndAddAgent({ commit, state }, { username, groups} ) {
        try {
            const agent = await LivechatService.createAndAddAgent(username, state.organization.uuid, groups);
            
            state.organization.agents.push(agent);
            commit('setOrganization', {
                ...state.organization
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to create agent");
        }
    },
    async updateAgent({ commit, state, dispatch, rootGetters }, { agent, routePath} ) {
        try {
            const updatedAgent = await LivechatService.updateAgent(agent);
            const agents = state.organization.agents;
            const currentAgent = agents.find(a => a.uuid === agent.uuid);
            
            Object.keys(updatedAgent).forEach(key => {
                currentAgent[key] = updatedAgent[key];
            });
            commit('setOrganization', {
                ...state.organization,
                agents: [...agents]
            });

            // Check if updated agent is current livechat agent and re-init livechat store
            const livechatAgent = rootGetters['livechat/agent'];
            if (livechatAgent && livechatAgent?.uuid === agent.uuid) {
                await dispatch('livechat/init', { 
                    botId: rootGetters['bots/currentBotId'],
                    routePath: routePath
                }, { root: true });
            }
        } catch (e) {
            console.error(e);
        }
    },
    async deleteAgent({ commit, state }, agentUuid ) {
        try {
            await LivechatService.deleteAgent(agentUuid);

            commit('setOrganization', {
                ...state.organization,
                agents: [...state.organization.agents.filter(a => a.uuid !== agentUuid)]
            });
        } catch (e) {
            console.error(e);
        }
    },
    /**
     * Adds a new group to the organization.
     * 
     * @param {object} context 
     * @param {import('@/services/livechat').Group} newGroup 
     */
    async createGroup({ commit, state }, newGroup) {
        try {
            const organization = await LivechatService.addGroupToOrganization(state.organization.uuid, newGroup);
            commit('setOrganization', {
                ...state.organization,
                groups: [...organization.groups]
            });
        } catch (e) {
            console.error(e);
        }
    },
    /**
     * Updates an existing group.
     * 
     * @param {object} context 
     * @param {import('@/services/livechat').Group} group 
     */
    async updateGroup({ commit, state }, group) {
        try {
            const organization = await LivechatService.updateGroup(state.organization.uuid, group);
            commit('setOrganization', {
                ...state.organization,
                groups: [...organization.groups]
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to update group");
        }
    },

    /**
     * Deletes a group from the organization.
     * 
     * @param {object} context 
     * @param {import('@/services/livechat').Group} group 
     */
    async deleteGroup({ commit, state }, group) {
        try {
            const result = await LivechatService.deleteGroup(state.organization.uuid, group);
            if (!result.success) {
                return result;
            }

            const organization = result.data;
            commit('setOrganization', {
                ...state.organization,
                groups: [...organization.groups]
            });

            return { success: true, message: "Group deleted successfully" };
        } catch (e) {
            console.error(e);
            throw new Error("Failed to delete group");
        }
    },
    async addOrganizationFile({ commit, state }, file) {
        // file: { name: string, url: string }
        try {
            const result = await LivechatService.addFileToOrganization(state.organization.uuid, file);
            commit('setOrganization', {
                ...state.organization,
                files: result.files || state.organization.files
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to add file");
        }
    },

    async removeOrganizationFile({ commit, state }, fileName) {
        try {
            const result = await LivechatService.removeFileFromOrganization(state.organization.uuid, fileName);
            commit('setOrganization', {
                ...state.organization,
                files: result.files || state.organization.files
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to remove file");
        }
    },

    async addOrganizationShortcode({ commit, state }, shortcode) {
        // shortcode: { code: string, text: string }
        try {
            const result = await LivechatService.addShortcodeToOrganization(state.organization.uuid, shortcode);
            commit('setOrganization', {
                ...state.organization,
                shortcodes: result.shortcodes || state.organization.shortcodes
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to add shortcode");
        }
    },

    async updateOrganizationShortcode({ commit, state }, { code, shortcode }) {
        // shortcode: { code: string, text: string }
        try {
            const result = await LivechatService.updateShortcodeForOrganization(state.organization.uuid, code, shortcode);
            commit('setOrganization', {
                ...state.organization,
                shortcodes: result.shortcodes || state.organization.shortcodes
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to update shortcode");
        }
    },

    async removeOrganizationShortcode({ commit, state }, code) {
        try {
            const result = await LivechatService.removeShortcodeFromOrganization(state.organization.uuid, code);
            commit('setOrganization', {
                ...state.organization,
                shortcodes: result.shortcodes || state.organization.shortcodes
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to remove shortcode");
        }
    },
    async createTag({ commit, state }, newTag) {
        try {
            const organization = await LivechatService.addOrganizationTag(state.organization.uuid, newTag);
            commit('setOrganization', {
                ...state.organization,
                tags: organization.tags
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to add tag");
        }
    },

    async updateTag({ commit, state }, { tagName, tagData }) {
        try {
            const organization = await LivechatService.updateOrganizationTag(state.organization.uuid, tagName, tagData);
            commit('setOrganization', {
                ...state.organization,
                tags: organization.tags
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to update tag");
        }
    },

    async deleteTag({ commit, state }, tagTitle) {
        try {
            const organization = await LivechatService.deleteOrganizationTag(state.organization.uuid, tagTitle);
            commit('setOrganization', {
                ...state.organization,
                tags: organization.tags
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to delete tag");
        }
    },

    async updateOrganization({ commit }, { uuid, organizationData}) {
        const updatedOrganization = await LivechatService.updateOrganizationLimited(uuid, organizationData);
        
        commit('setOrganization', updatedOrganization);
    },
    async updateOrganizationAdmin({ commit }, { uuid, organizationData}) {
        const updatedOrganization = await LivechatService.updateOrganizationAdmin(uuid, organizationData);
        
        commit('setOrganization', updatedOrganization);
    },

    async updateOrganizationFileDescription({ commit, state }, { fileName, description }) {
        try {
            const result = await LivechatService.updateFileDescription(state.organization.uuid, fileName, description);
            commit('setOrganization', {
                ...state.organization,
                files: result.files || state.organization.files
            });
        } catch (e) {
            console.error(e);
            throw new Error("Failed to update file description");
        }
    },
}

const mutations = {
    setOrganization(state, organization) {
        if (state.organization) {
            state.organization = {
                ...state.organization,
                ...organization
            }
        } else {
            state.organization = organization;
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}